import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {lingua} from '../../preferenze';
import {percorsoSezione, sezioneDelCatalogo, sezioneDelSito} from '../Casino/comune';
import {sezioniAccese} from '../../moduli/leggi';
import {tono} from '../Casino/Copertina';

/**
 * LE VINCITE VERE (casino-service, /public/casino/vincite): il pezzo comune
 * a «Vincendo ora» della Home e alla lista della Hall of Fame. Mr x, 23
 * ottobre: «la pagina hall of fame che sarebbe ultime grandi vincite,
 * vediamo come collegare il tutto».
 *
 * Il nome arriva gia' accorciato dal servizio; qui non si inventa niente:
 * finche' le vincite non arrivano, o se il servizio non risponde, chi usa
 * questo pezzo mostra lo scheletro di prima.
 */

/*
 * casino-service si chiama con un import() al primo uso e non in cima al
 * file: questo pezzo sta nella Home (Vetrina), e le prove delle pagine che
 * la disegnano non devono portarsi dietro axios e i18n per una fila di
 * vincite. Per lo stesso motivo l'importo si scrive qui come lo scrive
 * utils/conto.importo, invece di importarlo.
 */
const leggiVincite = (parametri) => import('../../services/casinoApi').then((m) => m.vincite(parametri));

export function importo(n, valuta) {
  const noti = {EUR: '€', USD: '$', GBP: '£'};
  const simbolo = valuta ? (noti[String(valuta).toUpperCase()] || String(valuta).toUpperCase()) : '';
  const testo = Number(n || 0).toLocaleString(lingua(), {minimumFractionDigits: 2, maximumFractionDigits: 2});
  return simbolo ? `${testo} ${simbolo}` : testo;
}

/**
 * Le vincite, rilette ogni `ogniMs` (0 = una volta). stato: «carica»,
 * «pronte» o «errore». Una nuova lettura che fallisce tiene le ultime arrivate.
 * Con `parametri` null non chiede niente: elenco vuoto (la skin non ha
 * nessuna sezione del casino).
 */
export function useVincite(parametri, ogniMs) {
  const chiave = JSON.stringify(parametri);
  const [esito, setEsito] = React.useState({stato: 'carica', dati: null});
  React.useEffect(() => {
    if (chiave === 'null') {
      setEsito({stato: 'pronte', dati: {vincite: [], sezioni: []}});
      return undefined;
    }
    let vivo = true;
    let timer = null;
    const leggi = () => {
      leggiVincite(JSON.parse(chiave))
          .then((dati) => { if (vivo) setEsito({stato: 'pronte', dati}); })
          .catch(() => { if (vivo) setEsito((e) => (e.stato === 'pronte' ? e : {stato: 'errore', dati: null})); })
          .finally(() => { if (vivo && ogniMs > 0) timer = setTimeout(leggi, ogniMs); });
    };
    setEsito({stato: 'carica', dati: null});
    leggi();
    return () => { vivo = false; if (timer) clearTimeout(timer); };
  }, [chiave, ogniMs]);
  return esito;
}

/** «adesso», «5 min fa», «3 ore fa», «ieri», «4 giorni fa», poi la data. */
export function quandoFa(iso, t, ora = Date.now()) {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const minuti = Math.max(0, Math.floor((ora - ms) / 60000));
  if (minuti < 1) return t('adesso');
  if (minuti < 60) return t('{{n}} min fa', {n: minuti});
  const ore = Math.floor(minuti / 60);
  if (ore < 24) return ore === 1 ? t('1 ora fa') : t('{{n}} ore fa', {n: ore});
  const giorni = Math.floor(ore / 24);
  if (giorni === 1) return t('ieri');
  if (giorni < 30) return t('{{n}} giorni fa', {n: giorni});
  return new Date(ms).toLocaleDateString(lingua(), {day: 'numeric', month: 'short', year: 'numeric'});
}

/** «×920» o niente; sopra mille senza decimali. */
export function moltiplicatoreTesto(m) {
  if (m === null || m === undefined || !Number.isFinite(m) || m <= 0) return '';
  return '×' + (m >= 1000 ? Math.round(m).toLocaleString() : String(m).replace('.', ','));
}

/** La copertina piccola del gioco; se non c'e' o non arriva, il colore del gioco con la sua iniziale. */
export function Miniatura({vincita, classe}) {
  const [fallita, setFallita] = React.useState(false);
  const immagine = vincita.copertina && !fallita;
  return (
    <span className={classe + ' vin-miniatura' + (immagine ? '' : ' senza')} style={{'--h': tono(vincita.giocoId || vincita.gioco)}} aria-hidden={'true'}>
      {immagine
        ? <img src={vincita.copertina} alt={''} loading={'lazy'} onError={() => setFallita(true)}/>
        : (vincita.gioco || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

/** Apre il gioco della vincita, se si sa quale. */
export function useApriGioco() {
  const navigate = useNavigate();
  return (v) => {
    if (v.giocoId) navigate(percorsoSezione(sezioneDelSito(v.sezione)) + '/gioco/' + encodeURIComponent(v.giocoId));
  };
}

/** Una vincita della fila di «Vincendo ora». */
export function VincitaInFila({vincita}) {
  const {t} = useTranslation();
  const apri = useApriGioco();
  const molt = moltiplicatoreTesto(vincita.moltiplicatore);
  return (
    <button type={'button'} className={'vet-vincita'} onClick={() => apri(vincita)} disabled={!vincita.giocoId}
      title={vincita.gioco}>
      <Miniatura vincita={vincita} classe={'vet-vincita-gioco'}/>
      <span className={'vet-vincita-testi'}>
        <span className={'vet-vincita-chi'}>{vincita.giocatore} · {quandoFa(vincita.quando, t)}</span>
        <strong className={'vet-vincita-quanto'}>{importo(vincita.importo, vincita.valuta)}{molt && <em> {molt}</em>}</strong>
        <span className={'vet-vincita-dove'}>{vincita.gioco}</span>
      </span>
    </button>
  );
}

export const PERIODI = ['settimana', 'mese', 'anno', 'sempre'];

/**
 * Le sezioni del sito le cui vincite stanno in casino-service. Lo sport
 * (sport, liveSport, sportBetby) non c'e': le schedine sono nel
 * ticket-service, e la sua scheda arrivera' quando lo sport sara' acceso.
 */
export const SEZIONI_DEL_CASINO = ['casino', 'liveCasino', 'crashGame', 'virtual'];

/** Le sezioni del casino accese per questa skin, nell'ordine di SEZIONI_DEL_CASINO. */
export function sezioniDellaSkin() {
  return sezioniAccese()
      .filter((s) => SEZIONI_DEL_CASINO.includes(s.chiave))
      .sort((a, b) => SEZIONI_DEL_CASINO.indexOf(a.chiave) - SEZIONI_DEL_CASINO.indexOf(b.chiave));
}

/**
 * Per «Tutte» e per «Vincendo ora»: le sezioni della skin come le chiede
 * casino-service («casino,liveCasino»), cosi' una skin con il solo Casino'
 * vede solo le vincite del Casino' (Mr x, 23 ottobre). Undefined = tutte e
 * quattro accese, niente filtro; null = nessuna, niente da chiedere.
 */
export function sezioniDaChiedere() {
  const della = sezioniDellaSkin();
  if (della.length === 0) return null;
  if (della.length === SEZIONI_DEL_CASINO.length) return undefined;
  return della.map((s) => sezioneDelCatalogo(s.chiave)).join(',');
}

/**
 * Le schede delle sezioni: quelle della skin che hanno giochi nel catalogo
 * (`delCatalogo`, dalla risposta). Con una sola, nessuna scheda: «Tutte»
 * e' gia' lei.
 */
export function schedeSezioni(delCatalogo) {
  const ci = new Set(delCatalogo || []);
  const schede = sezioniDellaSkin().filter((s) => ci.has(sezioneDelCatalogo(s.chiave)));
  return schede.length > 1 ? schede : [];
}

/**
 * LE GRANDI VINCITE della Hall of Fame: le piu' grandi del periodo, con le
 * schede Settimana / Mese / Anno / Sempre (si parte da quella scelta nel
 * backoffice) e, sopra, una scheda per sezione: Tutte, Casino', Live
 * casino, Crash game... (Mr x, 23 ottobre: «dividilo per sezioni»). Prima
 * che arrivino, o se il servizio non risponde, resta lo scheletro che
 * c'era: `scheletro` lo disegna.
 */
export function GrandiVincite({periodo, quante, minimo, scheletro}) {
  const {t} = useTranslation();
  const apri = useApriGioco();
  const [scelto, setScelto] = React.useState(PERIODI.includes(periodo) ? periodo : 'mese');
  React.useEffect(() => { if (PERIODI.includes(periodo)) setScelto(periodo); }, [periodo]);
  const [sezione, setSezione] = React.useState('tutte');
  const tutte = sezioniDaChiedere();
  const {stato, dati} = useVincite(tutte === null ? null : {
    modo: 'grandi', periodo: scelto, quante, minimo: minimo > 0 ? minimo : undefined,
    sezione: sezione === 'tutte' ? tutte : sezioneDelCatalogo(sezione),
  }, 0);
  /* le sezioni del catalogo restano quelle dell'ultima risposta: mentre la
     scheda nuova carica, le schede non spariscono */
  const [delCatalogo, setDelCatalogo] = React.useState(null);
  React.useEffect(() => { if (stato === 'pronte') setDelCatalogo(dati.sezioni); }, [stato, dati]);
  const schede = schedeSezioni(delCatalogo);
  const nomeSezione = (chiave) => {
    const s = schede.find((x) => sezioneDelCatalogo(x.chiave) === chiave);
    return s ? t(s.nome) : '';
  };
  const nomi = {settimana: t('Settimana'), mese: t('Mese'), anno: t('Anno'), sempre: t('Sempre')};
  if (stato === 'errore') return scheletro;
  const lista = stato === 'pronte' ? dati.vincite : null;
  return (
    <div className={'pg-scheletro pg-classifica pg-vincite'} aria-label={t('Ultime grandi vincite')}>
      <div className={'pg-classifica-testa'}>
        <strong>{t('Ultime grandi vincite')}</strong>
        <span className={'pg-vincite-periodi'} role={'tablist'} aria-label={t('Periodo')}>
          {PERIODI.map((p) => (
            <button key={p} type={'button'} role={'tab'} aria-selected={p === scelto}
              className={'pg-vincite-periodo' + (p === scelto ? ' scelto' : '')} onClick={() => setScelto(p)}>{nomi[p]}</button>
          ))}
        </span>
      </div>
      {schede.length > 0 && (
        <div className={'pg-vincite-sezioni'} role={'tablist'} aria-label={t('Sezione')}>
          {[{chiave: 'tutte', nome: 'Tutte'}].concat(schede).map((s) => (
            <button key={s.chiave} type={'button'} role={'tab'} aria-selected={s.chiave === sezione}
              className={'pg-vincite-sezione' + (s.chiave === sezione ? ' scelta' : '')} onClick={() => setSezione(s.chiave)}>{t(s.nome)}</button>
          ))}
        </div>
      )}
      {lista === null && (
        <ul className={'pg-classifica-scheletro'} aria-busy={'true'} aria-label={t('Vincite in arrivo')}>
          {[0, 1, 2, 3].map((i) => <li key={i} className={'pg-classifica-vuota'}/>)}
        </ul>
      )}
      {lista !== null && lista.length === 0 && (
        <p className={'pg-vincite-nessuna'}>{sezione === 'tutte'
          ? t('Ancora nessuna grande vincita in questo periodo.')
          : t('Ancora nessuna grande vincita in questa sezione e in questo periodo.')}</p>
      )}
      {lista !== null && lista.length > 0 && (
        <ol className={'pg-vincite-lista'} aria-label={t('Ultime grandi vincite')}>
          {lista.map((v, i) => (
            <li key={v.id} className={'pg-vincita'}>
              <span className={'pg-vincita-posto'}>{i + 1}</span>
              <Miniatura vincita={v} classe={'pg-vincita-gioco'}/>
              <span className={'pg-vincita-testi'}>
                <button type={'button'} className={'pg-vincita-nome'} onClick={() => apri(v)} disabled={!v.giocoId}>{v.gioco || '—'}</button>
                <span className={'pg-vincita-chi'}>
                  {v.giocatore} · {quandoFa(v.quando, t)}
                  {sezione === 'tutte' && nomeSezione(v.sezione) && <> · {nomeSezione(v.sezione)}</>}
                </span>
              </span>
              <span className={'pg-vincita-quanto'}>
                <strong>{importo(v.importo, v.valuta)}</strong>
                {moltiplicatoreTesto(v.moltiplicatore) && <em>{moltiplicatoreTesto(v.moltiplicatore)}</em>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const leggiRecord = (parametri) => import('../../services/casinoApi').then((m) => m.record(parametri));

/* Per «Tutte»: il record migliore fra le sezioni (le grandi stanno gia' in una valuta). */
const migliore = (record, campo, misura) => record.map((r) => r[campo]).filter(Boolean)
    .reduce((a, v) => (a === null || misura(v) > misura(a) ? v : a), null);

/**
 * I RECORD DI SEMPRE della Hall of Fame (Mr x, 23 ottobre, scelta A): la
 * vincita piu' grande e il moltiplicatore piu' alto da sempre, disegnati
 * come le «Ultime grandi vincite» (Mr x: «come le ultime grandi vincite»):
 * lo stesso riquadro, le schede delle sezioni della skin, due righe.
 * «Tutte» mostra il migliore fra le sezioni. Arrivano dal casino: finche'
 * non arrivano resta lo scheletro, e se il casino non risponde la parte non
 * si vede (niente di inventato).
 */
export function RecordDiSempre() {
  const {t} = useTranslation();
  const apri = useApriGioco();
  const sezioni = sezioniDaChiedere();
  const chiave = sezioni === null ? null : JSON.stringify({sezione: sezioni});
  const [esito, setEsito] = React.useState({stato: 'carica', dati: null});
  const [sezione, setSezione] = React.useState('tutte');
  React.useEffect(() => {
    if (chiave === null) return undefined;
    let vivo = true;
    setEsito({stato: 'carica', dati: null});
    leggiRecord(JSON.parse(chiave))
        .then((dati) => { if (vivo) setEsito({stato: 'pronte', dati}); })
        .catch(() => { if (vivo) setEsito({stato: 'errore', dati: null}); });
    return () => { vivo = false; };
  }, [chiave]);
  if (chiave === null || esito.stato === 'errore') return null;
  const record = (esito.stato === 'pronte' && esito.dati && esito.dati.record) || [];
  const schede = schedeSezioni(record.map((r) => r.sezione));
  const nomeSezione = (chiave) => {
    const s = schede.find((x) => sezioneDelCatalogo(x.chiave) === chiave);
    return s ? t(s.nome) : '';
  };
  const scelta = sezione === 'tutte' || !schede.some((s) => s.chiave === sezione) ? 'tutte' : sezione;
  const dellaScelta = scelta === 'tutte'
    ? {grande: migliore(record, 'grande', (v) => v.importo), moltiplicatore: migliore(record, 'moltiplicatore', (v) => v.moltiplicatore)}
    : record.find((r) => r.sezione === sezioneDelCatalogo(scelta)) || {};
  const righe = [
    {chiave: 'grande', etichetta: t('Vincita più grande'), icona: '🏆', vincita: dellaScelta.grande},
    {chiave: 'moltiplicatore', etichetta: t('Moltiplicatore più alto'), icona: '🚀', vincita: dellaScelta.moltiplicatore},
  ].filter((r) => r.vincita);
  return (
    <section className={'pg-scheletro pg-classifica pg-vincite pg-record'} aria-label={t('Record di sempre')}>
      <div className={'pg-classifica-testa'}>
        <strong>{t('Record di sempre')}</strong>
      </div>
      {schede.length > 0 && (
        <div className={'pg-vincite-sezioni'} role={'tablist'} aria-label={t('Sezione dei record')}>
          {[{chiave: 'tutte', nome: 'Tutte'}].concat(schede).map((s) => (
            <button key={s.chiave} type={'button'} role={'tab'} aria-selected={s.chiave === scelta}
              className={'pg-vincite-sezione' + (s.chiave === scelta ? ' scelta' : '')} onClick={() => setSezione(s.chiave)}>{t(s.nome)}</button>
          ))}
        </div>
      )}
      {esito.stato !== 'pronte' && (
        <ul className={'pg-classifica-scheletro'} aria-busy={'true'} aria-label={t('Record in arrivo')}>
          {[0, 1].map((i) => <li key={i} className={'pg-classifica-vuota'}/>)}
        </ul>
      )}
      {esito.stato === 'pronte' && righe.length === 0 && (
        <p className={'pg-vincite-nessuna'}>{scelta === 'tutte'
          ? t('Ancora nessun record.')
          : t('Ancora nessun record in questa sezione.')}</p>
      )}
      {righe.length > 0 && (
        <ol className={'pg-vincite-lista'} aria-label={t('Record di sempre')}>
          {righe.map(({chiave: quale, etichetta, icona, vincita: v}) => {
            const molt = moltiplicatoreTesto(v.moltiplicatore);
            const soldi = importo(v.importo, v.valuta);
            return (
              <li key={quale} className={'pg-vincita pg-record-' + quale}>
                <span className={'pg-vincita-posto'} aria-hidden={'true'}>{icona}</span>
                <Miniatura vincita={v} classe={'pg-vincita-gioco'}/>
                <span className={'pg-vincita-testi'}>
                  <button type={'button'} className={'pg-vincita-nome'} onClick={() => apri(v)} disabled={!v.giocoId}>{v.gioco || '—'}</button>
                  <span className={'pg-vincita-chi'}>
                    {etichetta} · {v.giocatore} · {quandoFa(v.quando, t)}
                    {scelta === 'tutte' && nomeSezione(v.sezione) && <> · {nomeSezione(v.sezione)}</>}
                  </span>
                </span>
                <span className={'pg-vincita-quanto'}>
                  <strong>{quale === 'moltiplicatore' && molt ? molt : soldi}</strong>
                  {quale === 'moltiplicatore' ? molt && <em>{soldi}</em> : molt && <em>{molt}</em>}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
